import React, { useMemo } from 'react';
import type { ComponentType } from 'react';

import type { ReadCallback, WithT } from 'i18next';
import { createInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useTranslation as defaultUseTranslation } from 'react-i18next';

import { useOnlyLocale } from '@adsk/alloy-react-locale';

import { useCheckSuspenseSetup } from './hooks/useSuspense';
import config from './i18next.config';
import type {
  CreateI18nInstanceOptions,
  CreateLocaleLazyImporterOptions,
  CreateUseTranslationHookOptions,
  InitLocalizationOptions,
  LazyLocaleError,
  Resources,
  ResourcesFormatted,
  UseTranslation,
  UseTranslationNamespace,
  UseTranslationOptions,
  WithTranslation,
} from './types';

const LOCALE_LAZY_LOAD_ERROR = 'localeLazyImporter_catch';

export const formatResources = (
  resources: Resources,
  enableMessageKeyHandling?: boolean,
): ResourcesFormatted => {
  const resourcesFormatted = Object.entries(resources).reduce(
    (rFormatted, [key, valueObj]) => {
      if (typeof valueObj === 'string') {
        rFormatted[key] = valueObj;
      } else if (enableMessageKeyHandling && valueObj.message) {
        // Need to reformat the locales because i18next doesn't like the description
        // that we sometimes add to help the translators out.
        rFormatted[key] = valueObj?.message ?? '';
      } else {
        rFormatted[key] = formatResources(valueObj);
      }
      return rFormatted;
    },
    {} as ResourcesFormatted,
  );

  return resourcesFormatted;
};

// creates an importer for an app/library's locale files, create a separate instance for each app or library
const createLocaleLazyImporter = ({
  getLocale,
  enableMessageKeyHandling,
  initOptions,
}: CreateLocaleLazyImporterOptions) => {
  /**
   * Lazily imports translated strings for a language, then passes the result back to i18next.
   * @param language IETF BCP 47 language tag
   */
  const localeLazyImporter = (
    language: string,
    namespace: string,
    callback: ReadCallback,
  ) => {
    getLocale(language, namespace)
      .then((resources) => {
        const resourcesFormatted = formatResources(
          resources,
          enableMessageKeyHandling,
        );
        callback(null, resourcesFormatted);
      })
      .catch((error) => {
        if (initOptions?.debug) {
          console.error(error);
        }
        // No localization for this language, i18next will falling back to less specific locale...
        error.type = LOCALE_LAZY_LOAD_ERROR;
        callback(error, null);
      });
  };

  return localeLazyImporter;
};

// creates an instance if i18next, need one for every locale importer (one per app)
const createI18nInstance = ({
  localeLazyImporter,
  translationFormat = 'default',
  plugins = [],
  initOptions = config,
}: CreateI18nInstanceOptions) => {
  let instance = createInstance();

  if (translationFormat === 'ICU') {
    instance = instance.use(ICU);
  }

  plugins.forEach((plugin) => {
    instance.use(plugin);
  });

  instance
    .use(LanguageDetector)
    .use(resourcesToBackend(localeLazyImporter))
    .init(initOptions, (errors) => {
      if (errors) {
        errors.forEach((error: LazyLocaleError) => {
          if (error && error.type === LOCALE_LAZY_LOAD_ERROR) {
            // don't pollute log, this is expected if locale files are less specific
            // example: `es.json` instead of `es-ES.json`
            return;
          }

          console.error(error);
        });
      }
    });
  return instance;
};

// creates instance specific versions of the useTranslation hook and withTranslation HOC
// should have one instance for a library and one for an app
const createUseTranslation = ({
  i18nInstance,
  internalUsage,
}: CreateUseTranslationHookOptions): {
  useTranslation: UseTranslation;
  withTranslation: WithTranslation<object>;
} => {
  const useTranslation: UseTranslation = (
    namespace?: UseTranslationNamespace,
    options?: UseTranslationOptions,
  ) => {
    const { locale } = useOnlyLocale();
    useCheckSuspenseSetup({ internalUsage });

    /**
     * Order of language preference:
     * 1. user provided via options.lng (this probably won't be used)
     * 2. locale returned from locale provider
     * 3. locale returned from i18next-browser-languagedetector
     */
    const translationOptions = useMemo(
      () => (options?.lng ? options : { ...options, lng: locale }),
      [locale, options],
    );

    return defaultUseTranslation(namespace, {
      i18n: i18nInstance,
      ...translationOptions,
    }) as ReturnType<typeof defaultUseTranslation>;
  };

  const withTranslation: WithTranslation<object> =
    <WrappedComponentProps extends object>() =>
    (Component: ComponentType<WrappedComponentProps & WithT>) => {
      const Wrapper = (ownProps: WrappedComponentProps) => {
        const { t } = useTranslation();
        const injectProps: WithT = {
          t,
        };
        const combinedProps = {
          ...ownProps,
          ...injectProps,
        };

        return <Component {...combinedProps} />;
      };

      return Wrapper;
    };

  return {
    useTranslation,
    withTranslation,
  };
};

export const initLocalization = ({
  getLocale,
  translationFormat,
  plugins,
  initOptions,
  internalUsage = false,
  enableMessageKeyHandling = false,
}: InitLocalizationOptions): {
  useTranslation: UseTranslation;
  withTranslation: WithTranslation<object>;
} => {
  const lazyLocaleImporter = createLocaleLazyImporter({
    getLocale,
    enableMessageKeyHandling,
    initOptions,
  });
  const i18nInstance = createI18nInstance({
    localeLazyImporter: lazyLocaleImporter,
    translationFormat,
    plugins,
    initOptions,
    internalUsage,
  });
  return createUseTranslation({ i18nInstance, internalUsage });
};
